import React, { useState, useEffect } from 'react';
import { FormContext } from 'quote/FormContext';
import { PolicyActionContext, PolicyDataContext } from './PolicyContext';
import { MyPolicy, QuoteFromOliver, sortProductsByName } from './utils';
import classNames from 'classnames';
import { ReactComponent as IconLoad } from 'assets/icons/icon-load.svg';
import { NavRoutes } from 'common/utils';
import { useHistory } from 'react-router-dom';
import oliverAPI from 'api/OliverAPI';
import { Alert, Tooltip } from 'antd';

interface IProps {
	primaryQuote: QuoteFromOliver[];
	spouseQuote?: QuoteFromOliver[];
	isPrimaryQuoteLoading?: boolean;
	isSpouseQuoteLoading?: boolean;
	isLoadingProducts?: boolean;
	loadPolicies?: (promoCode?: string) => void;
}

interface PromoCodeState {
	code: string;
	isValidating: boolean;
	isValid?: boolean;
	message?: string;
	validatedCode?: string;
	discountPercentage?: number;
	maxAmount?: number;
	applyToMember?: boolean;
	applyToSpouse?: boolean;
	applyToCandidate?: boolean;
	description?: string;
}

export const SideBarComponent: React.FC<IProps> = (props: IProps) => {
	const { primaryQuote, spouseQuote, isPrimaryQuoteLoading, isSpouseQuoteLoading, isLoadingProducts, loadPolicies } = props;
	const history = useHistory();
	const FormCtx = React.useContext(FormContext);
	const { initialUserData: primaryUserData } = FormCtx;
	const PolicyDataCtx = React.useContext(PolicyDataContext);
	const PolicyActionCtx = React.useContext(PolicyActionContext);

	const [promoState, setPromoState] = useState<PromoCodeState>({
		code: '',
		isValidating: false
	});
	const [promoInput, setPromoInput] = useState<string>(''); // new input state

	const handlePromoCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setPromoInput(e.target.value.toUpperCase());
	};

	const validatePromoCode = async () => {
		if (!promoInput.trim()) {
			setPromoState({
				...promoState,
				isValid: false,
				message: 'Please enter a promo code'
			});
			return;
		}

		setPromoState({
			...promoState,
			isValidating: true
		});

		try {
			// Check if promotion is valid for primary applicant
			const primaryResponse = await oliverAPI.validatePromoCode({
				code: promoInput,
				cpaStatus: FormCtx.personalData?.cpaStatus,
				isSpouse: false
			});

			// Check if promotion is valid for spouse quote (if spouse is included)
			const hasSpouse = FormCtx.spouseApplicantId !== undefined && FormCtx.spouseApplicantId !== '';
			let spouseResponse = null;
			if (hasSpouse) {
				spouseResponse = await oliverAPI.validatePromoCode({
					code: promoInput,
					cpaStatus: FormCtx.personalData?.cpaStatus,
					isSpouse: true
				});
			}

			// The promotion is valid if it's valid for either primary or spouse (or both)
			const isValidForPrimary = primaryResponse.isValid;
			const isValidForSpouse = spouseResponse?.isValid || false;
			const isValidOverall = isValidForPrimary || isValidForSpouse;

			if (isValidOverall) {
				// Use the primary response for the main promotion details
				const response = primaryResponse;

				let restrictionMessage = '';
				let isValidForThisPolicy = true;

				if (!isValidForThisPolicy) {
					setPromoState({
						...promoState,
						isValidating: false,
						isValid: false,
						message: restrictionMessage
					});
					return;
				}

				setPromoState({
					...promoState,
					isValidating: false,
					isValid: true,
					message: `Promo code ${response.code} successfully applied`,
					validatedCode: response.code,
					discountPercentage: response.discountPercentage,
					maxAmount: response.maxAmount,
					applyToMember: response.applyToMember,
					applyToSpouse: response.applyToSpouse,
					applyToCandidate: response.applyToCandidate,
					description: response.description
				});
				setPromoInput(''); // clear input field

				PolicyActionCtx.setCurrentPromoCode(response.code || '');

				if (loadPolicies) {
					loadPolicies(response.code);
				}
			} else {
				// Neither primary nor spouse validation passed
				// Show the more specific error message
				const errorMessage = !isValidForPrimary ? primaryResponse.message : spouseResponse?.message;
				setPromoState({
					...promoState,
					isValidating: false,
					isValid: false,
					message: errorMessage || 'Invalid promo code'
				});
			}
		} catch (error) {
			setPromoState({
				...promoState,
				isValidating: false,
				isValid: false,
				message: 'Error validating promo code'
			});
		}
	};

	return (
		<div className="shadow-sm rounded d-flex flex-column mb-lg-4">
			<div className="border-bottom border-bottom-gray-500 small mb-2">
				<div className="px-3 pt-3">
					<h3 className="mb-3">{primaryUserData?.firstName || ''} {primaryUserData?.lastName || ''}'s Policy Summary</h3>
					{PolicyDataCtx.policy.length === 0 && <p className="mb-3">Start adding policies to your plan and we’ll calculate how much it will cost you monthly.</p>}
					{sortProductsByName(PolicyDataCtx.policy).map((item: MyPolicy, index: number) => {
						const quoteItem = primaryQuote.find((val: QuoteFromOliver) => val.id === item.policyId);
						const isTermLife = PolicyActionCtx.productTitle(item.policyId) === 'Term Life';
						return (
							<React.Fragment key={`policy-item-${index}`}>
								<div className={classNames('d-flex justify-content-between mb-2 align-items-start')}>
									<span>{PolicyActionCtx.productTitle(item.policyId)}</span>
									{!PolicyDataCtx.isDirty && primaryQuote.length > 0 && quoteItem !== undefined ? (
										<span>${quoteItem?.premium?.toFixed(2).toLocaleString()} /mo</span>
									) : (
										<div className="bg-gray-300 text-gray-700 rounded px-3" ><IconLoad className={classNames('policy-spinner', isPrimaryQuoteLoading && 'spin')} /></div>
									)}
								</div>
								{isTermLife && !isPrimaryQuoteLoading && PolicyDataCtx.policy.length !== 0 && PolicyDataCtx.quoteData?.discount_amount && PolicyDataCtx.quoteData?.discount_amount > 0 && (
									<div className="mb-2">
										<div className="d-flex justify-content-between">
											<span className="d-flex align-items-center">
												{PolicyDataCtx.quoteData.promotion_code} Discount
											</span>
											<span className="text-success">
												{promoState.description && (
													<Tooltip title={promoState.description}>
														<span className="mr-2 d-inline-flex align-items-center">
															<div className="promo-info-icon">
																i
															</div>
														</span>
													</Tooltip>
												)}
												-${PolicyDataCtx.quoteData.discount_amount?.toFixed(2)} /mo</span>
										</div>
									</div>
								)}
							</React.Fragment>
						);
					})}
					{PolicyDataCtx.policy.length !== 0 && (
						<div className="d-flex justify-content-between policy-summary-total my-3 pt-3">
							<span className="font-weight-bold">Total</span>
							{PolicyDataCtx.isDirty || primaryQuote.length === 0 ? (
								<div className="bg-gray-300 text-gray-700 rounded px-3"><IconLoad className={classNames('policy-spinner', isPrimaryQuoteLoading && 'spin')} /></div>
							) : (
								<span className="font-weight-bold">${PolicyDataCtx.quoteData.total?.toFixed(2).toLocaleString()} /mo</span>
							)}
						</div>
					)}
				</div>
			</div>

			{FormCtx.spouseApplicantId !== undefined && FormCtx.spouseApplicantId !== '' &&
				<React.Fragment>
					<div className="border-bottom border-bottom-gray-500 small">
						<div className="px-3 pt-3">
							<h3 className="mb-3">{FormCtx.spouseData?.spouseFirstName || ''} {FormCtx.spouseData?.spouseLastName || ''}'s Policy Summary</h3>
							{PolicyDataCtx.spousePolicy.length === 0 && <p className="mb-3">Start adding policies to your plan and we’ll calculate how much it will cost you monthly.</p>}
							{sortProductsByName(PolicyDataCtx.spousePolicy).map((item: MyPolicy, index: number) => {
								const spouseQuoteItem = spouseQuote?.find((val: QuoteFromOliver) => val.id === item.policyId);
								const isTermLife = PolicyActionCtx.spouseProductTitle(item.policyId) === 'Term Life';
								return (
									<React.Fragment key={`spouse-policy-item-${index}`}>
										<div className={classNames('d-flex justify-content-between mb-2 align-items-start')}>
											<span>{PolicyActionCtx.spouseProductTitle(item.policyId)}</span>
											{!PolicyDataCtx.spouseIsDirty && spouseQuote && spouseQuote.length > 0 && spouseQuoteItem !== undefined ? (
												<span>${spouseQuoteItem?.premium?.toFixed(2).toLocaleString()} /mo</span>
											) : (
												<div className="bg-gray-300 text-gray-700 rounded px-3" ><IconLoad className={classNames('policy-spinner', isSpouseQuoteLoading && 'spin')} /></div>
											)}
										</div>
										{/* Show discount row right after Term Life for spouse */}
										{isTermLife && !isSpouseQuoteLoading && PolicyDataCtx.spousePolicy.length !== 0 && PolicyDataCtx.spouseQuoteData?.discount_amount && PolicyDataCtx.spouseQuoteData?.discount_amount > 0 && (
											<div className="mb-2">
												<div className="d-flex justify-content-between">
													<span className="d-flex align-items-center">
														{PolicyDataCtx.spouseQuoteData.promotion_code} Promotion
													</span>
													<span className="text-success">
														{promoState.description && (
															<Tooltip title={promoState.description}>
																<span className="mr-2 d-inline-flex align-items-center">
																	<div className="promo-info-icon">
																		i
																	</div>
																</span>
															</Tooltip>
														)}
														-${PolicyDataCtx.spouseQuoteData.discount_amount?.toFixed(2)} /mo</span>
												</div>
											</div>
										)}
									</React.Fragment>
								);
							})}
							{PolicyDataCtx.spousePolicy.length !== 0 && (
								<div className="d-flex justify-content-between policy-summary-total my-3 pt-3">
									<span className="font-weight-bold">Total</span>
									{PolicyDataCtx.spouseIsDirty || spouseQuote === undefined || spouseQuote.length === 0 ? (
										<div className="bg-gray-300 text-gray-700 rounded px-3"><IconLoad className={classNames('policy-spinner', isSpouseQuoteLoading && 'spin')} /></div>
									) : (
										<span className="font-weight-bold">${PolicyDataCtx.spouseQuoteData.total?.toFixed(2).toLocaleString()} /mo</span>
									)}
								</div>
							)}
						</div>
					</div>


					{PolicyDataCtx.spousePolicy.length !== 0 && (
						<div className="d-flex justify-content-between border-bottom border-bottom-gray-700 my-3 px-3 pb-3">
							<span className="font-weight-bold">Combined Total</span>
							{PolicyDataCtx.spouseIsDirty || spouseQuote === undefined || spouseQuote.length === 0 ? (
								<div className="bg-gray-300 text-gray-700 rounded px-3">
									<IconLoad className={classNames('policy-spinner', (isSpouseQuoteLoading || isPrimaryQuoteLoading) && 'spin')} />
								</div>
							) : (
								<span className="font-weight-bold">
									${(
										(PolicyDataCtx.spouseQuoteData?.total || 0) +
										(PolicyDataCtx.quoteData?.total || 0)
									).toFixed(2).toLocaleString()} /mo
								</span>

							)}
						</div>
					)}

				</React.Fragment>
			}
			<div className="px-3 pt-3 pb-4 mb-n1">
				<h6 className="mb-2">Promo Code</h6>
				<div className="d-flex">
					<input
						type="text"
						className="form-control text-uppercase mr-2"
						placeholder="Enter code"
						value={promoInput}
						onChange={handlePromoCodeChange}
						disabled={promoState.isValidating}
					/>
					<button
						className="btn btn-outline-primary"
						onClick={validatePromoCode}
						disabled={promoState.isValidating || !promoInput.trim()}
						type="button"
					>
						{promoState.isValidating || isPrimaryQuoteLoading || isSpouseQuoteLoading ? (
							<React.Fragment>
								<IconLoad className="policy-spinner spin" />
							</React.Fragment>
						) : 'Apply'}
					</button>
				</div>
				{promoState.message && (
					<Alert
						className="mt-2"
						message={
							promoState.isValid && promoState.validatedCode ?
								<>Promo code <strong>{promoState.validatedCode}</strong> successfully applied.</> :
								promoState.message
								+ '.'}
						type={promoState.isValid ? 'success' : 'error'}
						showIcon
					/>
				)}
			</div>

			<div className="d-flex align-items-start flex-column">
				{spouseQuote !== undefined && spouseQuote.length !== 0 ? (
					PolicyDataCtx.isDirty || primaryQuote.length === 0 || PolicyDataCtx.spouseIsDirty || spouseQuote?.length === 0 ? (
						<button
							className={classNames('btn btn-primary align-self-stretch mb-2 mx-3', PolicyDataCtx.policy.length === 0 && 'disabled')}
							disabled={PolicyDataCtx.policy.length === 0 || isLoadingProducts === true || promoState.isValidating}
							onClick={() => loadPolicies && loadPolicies(promoState.validatedCode)}
						>
							{isPrimaryQuoteLoading || isSpouseQuoteLoading ? 'Loading premiums' : 'Calculate Premium'}
							{(isPrimaryQuoteLoading || isSpouseQuoteLoading) && <IconLoad className={classNames('text-white policy-spinner ml-2 spinner-grow-sm spin')} />}
						</button>
					) : (
						<React.Fragment>
							<button
								className="btn btn-primary align-self-stretch mb-2 mx-3"
								onClick={goToReview}
								disabled={promoState.isValidating}
							>
								{isPrimaryQuoteLoading || isSpouseQuoteLoading ? 'Loading premiums' : 'Review'}
								{(isPrimaryQuoteLoading || isSpouseQuoteLoading) && <IconLoad className={classNames('text-white policy-spinner ml-2 spinner-grow-sm spin')} />}
							</button>
							{!isPrimaryQuoteLoading && !isSpouseQuoteLoading &&
								<button className="btn btn-outline-primary align-self-stretch mb-2 mx-3" onClick={() => history.push(NavRoutes.QuoteYourDetails)}>
									Back
								</button>
							}
						</React.Fragment>
					)
				) : (
					PolicyDataCtx.isDirty || primaryQuote.length === 0 ? (
						<button
							className={classNames('btn btn-primary align-self-stretch mb-2 mx-3', PolicyDataCtx.policy.length === 0 && 'disabled')}
							disabled={PolicyDataCtx.policy.length === 0 || isLoadingProducts === true || promoState.isValidating}
							onClick={() => loadPolicies && loadPolicies(promoState.validatedCode)}
						>
							{isPrimaryQuoteLoading ? 'Loading premiums' : 'Calculate Premium'}
							{isPrimaryQuoteLoading && <IconLoad className={classNames('text-white policy-spinner ml-2 spinner-grow-sm spin')} />}
						</button>
					) : (
						<React.Fragment>
							<button
								className="btn btn-primary align-self-stretch mb-2 mx-3"
								onClick={goToReview}
								disabled={promoState.isValidating}
							>
								{isPrimaryQuoteLoading ? 'Loading premiums' : 'Review'}
								{isPrimaryQuoteLoading && <IconLoad className={classNames('text-white policy-spinner ml-2 spinner-grow-sm spin')} />}
							</button>
							{!isPrimaryQuoteLoading &&
								<button className="btn btn-outline-primary align-self-stretch mb-2 mx-3" onClick={() => history.push(NavRoutes.QuoteYourDetails)}>
									Back
								</button>
							}
						</React.Fragment>
					)
				)}
			</div>
		</div>

	);

	function goToReview() {
		if (isPrimaryQuoteLoading || isSpouseQuoteLoading) { return; }
		PolicyActionCtx.checkAndSetHasChildCI();
		PolicyActionCtx.checkAndSetHasChildDL();
		PolicyActionCtx.spouseCheckAndSetHasChildDLorCL();
		history.push(NavRoutes.QuoteReviewPolicy);
	}
};
